import React, { useCallback, useEffect, useMemo } from 'react';
import { pushEvent } from './clevertap';
import { useTrackingConfigContext } from './TrackingConfigProvider';
import {
    TrackingProvider,
    TrackingProviderProps,
    useTrackingContext,
} from './TrackingProvider';

/**
 * Provides page name for consumer components
 *
 * You can stack multiple TrackingProviders insed each other to combine page names
 *
 * By default it will send `View_Event` event when component is mounted
 * @param pageName - name of the page
 * @param trackPageVisit - send `View_Event` event
 * @example
 * <TrackingProvider pageName="parentPage">
 *     <TrackingProvider pageName="subPage">
 *         const { pageName, trackEvent } = useTrackingContext();
 *         // pageName is `parentPage_subPage` here
 *     </TrackingProvider>
 * </TrackingProvider>
 */
export const TrackingProviderV2: React.FCC<TrackingProviderProps> = ({
    children,
    pageName,
    trackPageVisit,
    hideInChild,
    viewType = 'page',
    trackUnmountEvent,
    customParams = {},
}) => {
    const { getPayload } = useTrackingConfigContext();
    const parentCtx = useTrackingContext();
    const getNamePrefix = useCallback(() => {
        if (!pageName) return '';
        return viewType === 'page'
            ? `${pageName}Page`
            : viewType === 'modal'
            ? `${pageName}Modal`
            : pageName;
    }, [viewType, pageName]);
    const locationPayload: { location?: string; screen?: string } =
        useMemo(() => {
            const namePrefix = getNamePrefix();
            if (!namePrefix) return { location: '' };
            return {
                location: namePrefix,
                screen:
                    parentCtx.pageName && !parentCtx.hideInChild
                        ? parentCtx.pageName
                        : undefined,
            };
        }, [getNamePrefix, parentCtx.hideInChild, parentCtx.pageName]);

    const trackEventV2 = useCallback(
        (
            eventType:
                | 'view'
                | 'close'
                | 'click'
                | 'success'
                | 'fail'
                | 'submit',
            eventPayload?: {
                name?: string;
                type?:
                    | 'Card'
                    | 'Button'
                    | 'Link'
                    | 'Page'
                    | 'Modal'
                    | 'Tab'
                    | 'Banner'
                    | 'Text'
                    | 'Slider'
                    | 'Input'
                    | 'Checkbox'
                    | 'Other'
                    | string;
            } & Record<string, unknown>,
        ) => {
            const extraPayload = getPayload();
            let event = 'View_Event';
            if (eventType === 'close') {
                event = 'Close_Event';
            } else if (eventType === 'click') {
                event = 'Click_Event';
            } else if (eventType === 'success') {
                event = 'Success_Event';
            } else if (eventType === 'fail') {
                event = 'Failure_Event';
            } else if (eventType === 'submit') {
                event = 'Submit_Event';
            }
            pushEvent(
                event,
                Object.fromEntries(
                    Object.entries({
                        ...extraPayload,
                        ...(eventType === 'view' || eventType === 'close'
                            ? {
                                  name: locationPayload.location,
                                  location: locationPayload.screen,
                              }
                            : locationPayload),
                        ...eventPayload,
                        ...customParams,
                    }).filter(([, v]) => v !== undefined),
                ),
            );
        },
        [locationPayload, getPayload, customParams],
    );

    const trackOpenCloseEvent = useCallback(
        (open: boolean) => {
            if (open) {
                trackEventV2('view', {
                    type:
                        viewType === 'page'
                            ? 'Page'
                            : viewType === 'modal'
                            ? 'Modal'
                            : viewType === 'banner'
                            ? 'Banner'
                            : viewType === 'tab'
                            ? 'Tab'
                            : 'Other',
                });
            } else {
                trackEventV2('close', {
                    type:
                        viewType === 'page'
                            ? 'Page'
                            : viewType === 'modal'
                            ? 'Modal'
                            : viewType === 'banner'
                            ? 'Banner'
                            : viewType === 'tab'
                            ? 'Tab'
                            : 'Other',
                });
            }
        },
        [trackEventV2, viewType],
    );

    useEffect(() => {
        if (!trackPageVisit || !pageName?.trim()) return;
        trackOpenCloseEvent(true);
        return () => {
            if (trackUnmountEvent) {
                trackOpenCloseEvent(false);
            }
        };
        //track page visit only on load
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <TrackingProvider
            hideInChild={hideInChild}
            pageName={pageName}
            trackPageVisit={false}
            trackingContextProps={{
                trackOpenCloseEvent,
                trackEventV2,
                getPageName: getNamePrefix,
            }}
            viewType={viewType}
        >
            {children}
        </TrackingProvider>
    );
};
